import React, { useLayoutEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
  keyframes,
} from "@mui/material";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import {
  getSignColor,
  formatCurrency,
  formatCompactCurrency,
  formatPercentPlain,
  formatPercentSigned,
  formatPercentColored,
  formatLongCapital,
  formatShortCapital,
  formatNetCapital,
  isValidNum,
} from "../../PerformanceDashboard/utility";
import { v2CardBg, v2CardBorder, v2LabelSx, v2PeriodTint, v2Accent } from "../v2Theme";

const cellSx = {
  borderBottom: "1px solid",
  py: 0.55,
  px: 0.7,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const exposurePulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(232, 232, 232, 0.0);
    opacity: 0.72;
  }
  50% {
    box-shadow: 0 0 10px 2px rgba(232, 232, 232, 0.35);
    opacity: 1;
  }
`;

const exposurePulseLight = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    opacity: 0.7;
  }
  50% {
    box-shadow: 0 0 8px 1px rgba(0, 0, 0, 0.18);
    opacity: 1;
  }
`;

const GroupCell = ({
  children,
  colSpan,
  align = "center",
  onMouseEnter,
  onMouseLeave,
  sx: sxProp,
}) => {
  const theme = useTheme();
  return (
    <TableCell
      align={align}
      colSpan={colSpan}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        ...cellSx,
        py: 0.7,
        borderBottom: "none",
        borderRight: `1px solid ${v2CardBorder(theme)}`,
        backgroundColor: v2CardBg(theme),
        "&:last-of-type": { borderRight: "none" },
        ...sxProp,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.08em",
          fontSize: "0.7rem",
          textTransform: "uppercase",
          ...v2LabelSx(theme),
        }}
      >
        {children}
      </Typography>
    </TableCell>
  );
};

/** Narrow hover handle when Exposure columns are collapsed. */
const ExposurePeekCell = ({
  header = false,
  onMouseEnter,
  onMouseLeave,
  groupEnd,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <TableCell
      align="center"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        ...cellSx,
        py: header ? 0.35 : 0.55,
        px: 0.15,
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        borderBottomColor: v2CardBorder(theme),
        borderRight: groupEnd ? `1px solid ${v2CardBorder(theme)}` : "none",
        backgroundColor: v2CardBg(theme),
        verticalAlign: "middle",
        cursor: "ew-resize",
      }}
    >
      {header ? (
        <Tooltip title="Hover to show exposure" placement="top" arrow>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              borderRadius: 1,
              color: v2Accent(theme),
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.04)",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.12)"
              }`,
              animation: `${isDark ? exposurePulse : exposurePulseLight} 2.2s ease-in-out infinite`,
            }}
          >
            <UnfoldMoreRoundedIcon
              sx={{ fontSize: "0.85rem", transform: "rotate(90deg)" }}
            />
          </Box>
        </Tooltip>
      ) : (
        <Box
          sx={{
            width: 3,
            height: 16,
            mx: "auto",
            borderRadius: 99,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.28)"
              : "rgba(0,0,0,0.16)",
          }}
        />
      )}
    </TableCell>
  );
};

const STRATEGY_COL_WIDTH = 150;

const HeadCell = ({
  children,
  align = "right",
  sticky,
  stickyLeft = 0,
  stickyWidth = 220,
  fitContent = false,
  groupEnd,
  sortId,
  order,
  orderBy,
  onSort,
  cellRef,
  onMouseEnter,
  onMouseLeave,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const active = Boolean(sortId) && orderBy === sortId;

  return (
    <TableCell
      ref={cellRef}
      align={align}
      sortDirection={active ? order : false}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        ...cellSx,
        py: 0.5,
        backgroundColor: v2CardBg(theme),
        borderBottomColor: v2CardBorder(theme),
        borderRight: groupEnd ? `1px solid ${v2CardBorder(theme)}` : "none",
        ...(sticky
          ? {
              position: "sticky",
              left: stickyLeft,
              zIndex: 3,
              ...(fitContent
                ? {
                    width: "1%",
                    whiteSpace: "nowrap",
                  }
                : {
                    minWidth: stickyWidth,
                    maxWidth: stickyWidth,
                    width: stickyWidth,
                  }),
            }
          : null),
      }}
    >
      <TableSortLabel
        active={active}
        direction={active ? order : "desc"}
        hideSortIcon
        onClick={(event) => {
          event.stopPropagation();
          if (sortId) onSort?.(sortId);
        }}
        sx={{
          ...v2LabelSx(theme),
          fontWeight: active ? 800 : 600,
          letterSpacing: "0.04em",
          fontSize: "0.78rem",
          cursor: "pointer",
          color: active
            ? isDark
              ? "#FFFFFF"
              : theme.palette.text.primary
            : "inherit",
          "&:hover": {
            color: isDark ? "#FFFFFF" : theme.palette.text.primary,
          },
          "&.Mui-active": {
            color: isDark ? "#FFFFFF" : theme.palette.text.primary,
          },
          "& .MuiTableSortLabel-icon": {
            display: "none",
          },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
};

const ValueCell = ({
  children,
  color,
  align = "right",
  groupEnd,
  strong,
  onMouseEnter,
  onMouseLeave,
}) => {
  const theme = useTheme();
  return (
    <TableCell
      align={align}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        ...cellSx,
        borderBottomColor: v2CardBorder(theme),
        borderRight: groupEnd ? `1px solid ${v2CardBorder(theme)}` : "none",
        color: color || "text.primary",
        fontWeight: strong ? 700 : 600,
        fontSize: "0.95rem",
      }}
    >
      {children || "—"}
    </TableCell>
  );
};

const PeriodCell = ({ value, isFactor }) => {
  const theme = useTheme();
  const num = Number(value);
  const showDash = isFactor || !isValidNum(value);
  const tint = showDash ? "transparent" : v2PeriodTint(theme, num >= 0);
  const color = showDash
    ? theme.palette.mode === "dark"
      ? "#C8C8C8"
      : theme.palette.text.secondary
    : getSignColor(num, theme);

  return (
    <TableCell
      align="center"
      sx={{
        ...cellSx,
        px: 0.45,
        borderBottomColor: v2CardBorder(theme),
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          minWidth: 44,
          justifyContent: "center",
          px: 0.45,
          py: 0.15,
          borderRadius: 1,
          backgroundColor: tint,
          color,
          fontWeight: 700,
          fontSize: "0.9rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {showDash ? "-" : formatPercentColored(value)}
      </Box>
    </TableCell>
  );
};

const TooltipMetric = ({ label, value, theme }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 2.5,
      py: 0.35,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        fontSize: "0.78rem",
        letterSpacing: "0.03em",
        ...v2LabelSx(theme),
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        fontSize: "0.88rem",
        fontVariantNumeric: "tabular-nums",
        color: "text.primary",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const NameCellTooltip = ({ row, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Tooltip
      arrow
      placement="right"
      enterDelay={250}
      enterNextDelay={120}
      title={
        <Box sx={{ py: 0.75, px: 0.25, minWidth: 240, maxWidth: 360 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.95rem",
              lineHeight: 1.35,
              whiteSpace: "normal",
              wordBreak: "break-word",
              color: "text.primary",
              mb: 1,
            }}
          >
            {row.strategyName}
          </Typography>
          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: v2CardBorder(theme),
              pt: 0.75,
            }}
          >
            <TooltipMetric
              label="AUM"
              value={formatCompactCurrency(row.aum)}
              theme={theme}
            />
            <TooltipMetric
              label="Long Capital"
              value={formatLongCapital(row.longCapital)}
              theme={theme}
            />
            <TooltipMetric
              label="Short Capital"
              value={formatShortCapital(row.shortCapital)}
              theme={theme}
            />
            <TooltipMetric
              label="Net Capital"
              value={formatNetCapital(row.netCapital)}
              theme={theme}
            />
          </Box>
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            p: 1.25,
            maxWidth: 380,
            backgroundColor: v2CardBg(theme),
            color: theme.palette.text.primary,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.36)" : v2CardBorder(theme)}`,
            boxShadow: isDark
              ? "0 8px 24px rgba(0,0,0,0.45)"
              : "0 8px 24px rgba(0,0,0,0.12)",
          },
        },
        arrow: {
          sx: {
            color: v2CardBg(theme),
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

const StrategyPerformanceTable = ({
  rows = [],
  order = "desc",
  orderBy = "plPer",
  onRequestSort,
  onFundClick,
  onStrategyClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const headProps = { order, orderBy, onSort: onRequestSort };
  const fundHeadRef = useRef(null);
  const [fundColWidth, setFundColWidth] = useState(96);
  const [exposureOpen, setExposureOpen] = useState(false);
  const exposureLeaveTimer = useRef(null);

  const openExposure = useCallback(() => {
    if (exposureLeaveTimer.current) {
      clearTimeout(exposureLeaveTimer.current);
      exposureLeaveTimer.current = null;
    }
    setExposureOpen(true);
  }, []);

  const closeExposure = useCallback(() => {
    if (exposureLeaveTimer.current) clearTimeout(exposureLeaveTimer.current);
    exposureLeaveTimer.current = setTimeout(() => {
      setExposureOpen(false);
      exposureLeaveTimer.current = null;
    }, 140);
  }, []);

  const exposureHoverProps = {
    onMouseEnter: openExposure,
    onMouseLeave: closeExposure,
  };

  useLayoutEffect(() => {
    const node = fundHeadRef.current;
    if (!node) return undefined;

    const update = () => {
      const width = Math.ceil(node.getBoundingClientRect().width);
      if (width > 0) setFundColWidth(width);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [rows]);

  useLayoutEffect(
    () => () => {
      if (exposureLeaveTimer.current) clearTimeout(exposureLeaveTimer.current);
    },
    []
  );

  if (!rows?.length) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: v2CardBg(theme),
          color: "text.primary",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No strategies match the current filters
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        borderRadius: 2,
        overflowX: "auto",
        border: "1px solid",
        borderColor: isDark ? "rgba(255,255,255,0.36)" : v2CardBorder(theme),
        backgroundColor: v2CardBg(theme),
        boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          // Collapsed: fill container so columns share space (no H-scroll).
          // Expanded: grow with content and allow scroll for exposure cols.
          width: exposureOpen ? "max-content" : "100%",
          minWidth: "100%",
        }}
      >
        <TableHead>
          <TableRow>
            <GroupCell align="left" colSpan={2}>
              Fund / Strategy
            </GroupCell>
            <GroupCell colSpan={4}>Capital</GroupCell>
            <GroupCell colSpan={5}>Today</GroupCell>
            <GroupCell colSpan={exposureOpen ? 3 : 1} {...exposureHoverProps}>
              Exposure
            </GroupCell>
            <GroupCell colSpan={6}>Periods</GroupCell>
          </TableRow>
          <TableRow>
            <HeadCell
              align="left"
              sticky
              stickyLeft={0}
              fitContent
              cellRef={fundHeadRef}
              sortId="fundName"
              {...headProps}
            >
              Fund
            </HeadCell>
            <HeadCell
              align="left"
              sticky
              stickyLeft={fundColWidth}
              stickyWidth={STRATEGY_COL_WIDTH}
              groupEnd
              sortId="strategyName"
              {...headProps}
            >
              Strategy
            </HeadCell>
            <HeadCell sortId="aum" {...headProps}>
              AUM
            </HeadCell>
            <HeadCell sortId="longCapital" {...headProps}>
              Long Cap
            </HeadCell>
            <HeadCell sortId="shortCapital" {...headProps}>
              Short Cap
            </HeadCell>
            <HeadCell groupEnd sortId="netCapital" {...headProps}>
              Net Cap
            </HeadCell>
            <HeadCell sortId="pL" {...headProps}>
              P(L)
            </HeadCell>
            <HeadCell sortId="plLong" {...headProps}>
              Long
            </HeadCell>
            <HeadCell sortId="plShort" {...headProps}>
              Short
            </HeadCell>
            <HeadCell sortId="longPerformance" {...headProps}>
              Long Perf
            </HeadCell>
            <HeadCell groupEnd sortId="shortPerformance" {...headProps}>
              Short Perf
            </HeadCell>
            {exposureOpen ? (
              <>
                <HeadCell sortId="sizeLong" {...headProps} {...exposureHoverProps}>
                  DA Long
                </HeadCell>
                <HeadCell sortId="sizeShort" {...headProps} {...exposureHoverProps}>
                  DA Short
                </HeadCell>
                <HeadCell
                  groupEnd
                  sortId="netAumPercent"
                  {...headProps}
                  {...exposureHoverProps}
                >
                  Net
                </HeadCell>
              </>
            ) : (
              <ExposurePeekCell header groupEnd {...exposureHoverProps} />
            )}
            <HeadCell align="center" sortId="pLYest" {...headProps}>
              Yest
            </HeadCell>
            <HeadCell align="center" sortId="plPer" {...headProps}>
              Today
            </HeadCell>
            <HeadCell align="center" sortId="pLMonth" {...headProps}>
              MTD
            </HeadCell>
            <HeadCell align="center" sortId="pLQuarter" {...headProps}>
              QTD
            </HeadCell>
            <HeadCell align="center" sortId="pLYear" {...headProps}>
              YTD
            </HeadCell>
            <HeadCell align="center" sortId="pLITD" {...headProps}>
              ITD
            </HeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            // SMAs reuse CPFactorBW as a bucket but have real fund-level period RoR.
            const isFactor = row.strategyName === "CPFactorBW" && !row.isSMA;
            const plSign = Number(row.plPer) || Number(row.pL) || 0;
            const accent = getSignColor(plSign, theme);

            const handleRowClick = () => {
              if (isFactor) {
                onFundClick?.(row.fundName);
              } else {
                onStrategyClick?.(row.fundName, row.strategyName);
              }
            };

            return (
              <TableRow
                key={`${row.fundName}-${row.strategyName}-${index}`}
                onClick={handleRowClick}
                hover
                sx={{
                  cursor: "pointer",
                  "& td": {
                    backgroundColor: v2CardBg(theme),
                  },
                  "&:hover td": {
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.03)",
                  },
                }}
              >
                <TableCell
                  onClick={(event) => {
                    event.stopPropagation();
                    onFundClick?.(row.fundName);
                  }}
                  sx={{
                    ...cellSx,
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    width: "1%",
                    whiteSpace: "nowrap",
                    borderBottomColor: v2CardBorder(theme),
                    borderLeft: `3px solid ${accent}`,
                    pl: 1,
                    pr: 1.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.92rem",
                      color: "text.primary",
                      whiteSpace: "nowrap",
                    }}
                    title={row.fundDisplayName || row.fundName}
                  >
                    {row.fundDisplayName || row.fundName}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    position: "sticky",
                    left: fundColWidth,
                    zIndex: 1,
                    minWidth: STRATEGY_COL_WIDTH,
                    maxWidth: STRATEGY_COL_WIDTH,
                    width: STRATEGY_COL_WIDTH,
                    overflow: "hidden",
                    borderBottomColor: v2CardBorder(theme),
                    borderRight: `1px solid ${v2CardBorder(theme)}`,
                    pl: 1,
                  }}
                >
                  <NameCellTooltip row={row}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.strategyName}
                      </Typography>
                    </Box>
                  </NameCellTooltip>
                </TableCell>
                <ValueCell>{formatCompactCurrency(row.aum)}</ValueCell>
                <ValueCell>{formatLongCapital(row.longCapital)}</ValueCell>
                <ValueCell>{formatShortCapital(row.shortCapital)}</ValueCell>
                <ValueCell groupEnd>{formatNetCapital(row.netCapital)}</ValueCell>
                <ValueCell color={getSignColor(row.pL, theme)}>
                  {formatCurrency(row.pL)}
                </ValueCell>
                <ValueCell color={getSignColor(row.plLong, theme)}>
                  {formatCurrency(row.plLong)}
                </ValueCell>
                <ValueCell color={getSignColor(row.plShort, theme)}>
                  {formatCurrency(row.plShort)}
                </ValueCell>
                <ValueCell color={getSignColor(row.longPerformance, theme)}>
                  {formatPercentColored(row.longPerformance)}
                </ValueCell>
                <ValueCell
                  groupEnd
                  color={getSignColor(row.shortPerformance, theme, true)}
                >
                  {formatPercentColored(row.shortPerformance)}
                </ValueCell>
                {exposureOpen ? (
                  <>
                    <ValueCell {...exposureHoverProps}>
                      {formatPercentPlain(row.sizeLong)}
                    </ValueCell>
                    <ValueCell {...exposureHoverProps}>
                      {formatPercentSigned(row.sizeShort)}
                    </ValueCell>
                    <ValueCell groupEnd {...exposureHoverProps}>
                      {formatPercentSigned(row.netAumPercent)}
                    </ValueCell>
                  </>
                ) : (
                  <ExposurePeekCell groupEnd {...exposureHoverProps} />
                )}
                <PeriodCell value={row.pLYest} />
                <PeriodCell value={row.plPer} />
                <PeriodCell value={row.pLMonth} isFactor={isFactor} />
                <PeriodCell value={row.pLQuarter} isFactor={isFactor} />
                <PeriodCell value={row.pLYear} isFactor={isFactor} />
                <PeriodCell value={row.pLITD} isFactor={isFactor} />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StrategyPerformanceTable;
